import Phaser from 'phaser';
import { SCENE, RESTORABLE_SCENES, VISIT_SCENES, assetUrl } from '../config.js';
import { gameState } from '../state/gameState.js';
import { migrateLocalTweaksToServer } from '../ui/ClothTweaks.js';
import { api } from '../api/client.js';
import { showLicenceModal } from '../ui/LicenceModal.js';
import { showDressingModal } from '../ui/DressingModal.js';
import { celebrate } from '../ui/celebration.js';
import { realtime } from '../api/realtime.js';
import { audioManager } from '../audio/AudioManager.js';
import { prioritiseFor } from '../ui/sceneLoading.js';
import { preloader } from './PreloadScene.js';
import { audioId } from '../assetManifest.mjs';
import { MUSIC_KEY } from '../audio/eventMap.js';
import { quickLogins } from '../auth/quickLogin.mjs';
import { readVisitSnapshot, SNAPSHOT_KEY } from '../state/autoUpdate.mjs';

// One-time wiring survives BootScene re-entry (login → BOOT after logout/login).
let outfitBroadcastWired = false;

export class BootScene extends Phaser.Scene {
  constructor() { super(SCENE.BOOT); }

  preload() {
    // Suppress the browser's native right-click menu so in-game right-click menus work.
    this.input.mouse?.disableContextMenu();
    // Our icons (ui/icons.mjs) are drawn in every scene's text, so they load with the game.
    this.load.atlas('icons', assetUrl('/assets/icons/icons.png'), assetUrl('/assets/icons/icons.json'));
    // What the riding art draws by (scripts/anim/bike/export.py): layer orders, wheels, sizes; the atlases
    // themselves load on demand (ui/bikeArt.js).
    this.load.json('bike_rig', assetUrl('/assets/bike/bike_rig.json'));
  }

  async create() {
    audioManager.wire(this);
    // Keep the background loader off the link while we log in / fetch state: on a slow
    // connection its downloads otherwise delay these small requests by many seconds.
    preloader.hold();
    try { await this._create(); } finally { preloader.unhold(); }
  }

  async _create() {
    // The background loader lives beside every scene from now on (staged loading).
    if (!this.scene.isActive(SCENE.PRELOAD)) this.scene.launch(SCENE.PRELOAD);
    this.scene.bringToTop(this.scene.key);

    // The music clip is the last file the background loader fetches; when it lands, start it
    // if audio is already unlocked (otherwise the 'unlocked' hook in wire() finds it cached).
    // preloader.whenReady() queues this call if PreloadScene hasn't run its create() yet (a cold
    // boot: `this.scene.launch()` above only QUEUES it, Phaser runs create() next game step) and
    // drains it once ready, so registering right away is safe on both cold boot and BootScene
    // re-entry after login. Can end up arming this twice across a login cycle — harmless,
    // startMusic() is idempotent (checks music.isPlaying before playing again).
    preloader.whenReady(audioId(MUSIC_KEY), () => { if (!audioManager.sound?.locked) audioManager.startMusic(); });
    // DEV-only size-tuning tool — reachable at /?sizelab, no login needed.
    if (import.meta.env.DEV && new URLSearchParams(location.search).has('sizelab')) {
      this.scene.start('SizeLabScene');
      return;
    }
    try {
      const me = await api.me();
      window.__myId = me?.player_id ?? null;
      window.__myName = me?.name ?? null;   // used on the driving licence card
      if (import.meta.env?.DEV) {
        // Dev-only hooks for driving the UI from the browser console / playwriter (see the
        // verify skill): the app's OWN module instances, which a dynamic import() of
        // '/src/…' does not give you (Vite hands the console a second module graph).
        window.__dev = { gameState, showLicenceModal, showDressingModal, celebrate };
      }
      window.__myCharacter = me?.character ?? 'natalka';
      let devLook = null;   // ?look={"skin":"s4"} tries a look without saving it (dev only)
      if (import.meta.env?.DEV) {
        try { devLook = JSON.parse(new URLSearchParams(location.search).get('look') ?? 'null'); } catch { devLook = null; }
      }
      window.__myLook = devLook ?? me?.look ?? null;
      await gameState.refresh();
      // No prioritiseFor() here: _resumeLastScene() below starts the saved scene, whose own
      // preload() claims its tier 0 and then calls prioritiseFor(this.scene.key). Doing it
      // here as well made the background loader start those same files a step earlier, and
      // the scene's loader — seeing them in flight, not cached — fetched every one twice.
      realtime.connect();
      window.__myEquipped = gameState.raw?.inventory?.equipped ?? null;
      quickLogins.setLook(window.__myId, { character: window.__myCharacter, equipped: window.__myEquipped, look: window.__myLook ?? null });   // keep her circle current
      // Broadcast wardrobe changes (equip / tweak) live to anyone sharing a room.
      // Boot runs again after an in-page login (LoginScene starts BOOT), so wire once.
      if (!outfitBroadcastWired) {
        outfitBroadcastWired = true;
        let lastOutfit = null;
        gameState.on((raw) => {
          const outfit = { equipped: raw?.inventory?.equipped ?? null, cloth_tweaks: raw?.cloth_tweaks ?? {},
                           look: window.__myLook ?? null };
          window.__myEquipped = outfit.equipped;
          const json = JSON.stringify(outfit);
          if (json !== lastOutfit) { lastOutfit = json; realtime.outfit(outfit); }
        });
      }
      await migrateLocalTweaksToServer();  // one-time: carry legacy localStorage tweaks up to the account
      this._resumeLastScene();
    } catch (e) {
      if (e?.status === 401) {
        // Not logged in: start streaming the default order (the yard first) while the form is up
        // (a phone fetches only the yard and the music: who logs in may go anywhere).
        prioritiseFor(SCENE.MAIN, { state: {}, equipped: null, login: true });
        this.scene.start(SCENE.LOGIN);
      } else {
        this.add.text(640, 400, 'Błąd połączenia. Spróbuj odświeżyć.', {
          fontFamily: 'system-ui', fontSize: '20px', color: '#ff8888',
        }).setOrigin(0.5);
      }
    }
  }

  // Resume the scene/position saved on the server (see locationTracker). Falls back
  // to the yard for unknown scenes; scenes keep their own guards (locked rooms, etc.).
  _resumeLastScene() {
    // A new-version reload during a visit (ui/autoUpdate.js): back at the friend's, once.
    let snap = null;
    try { snap = sessionStorage.getItem(SNAPSHOT_KEY); sessionStorage.removeItem(SNAPSHOT_KEY); } catch { /* blocked storage */ }
    const visit = snap && readVisitSnapshot(snap, Date.now(), VISIT_SCENES);
    if (visit) { this.scene.start(visit.scene, visit.data); return; }
    const loc = gameState.raw?.location;
    const scene = (loc && RESTORABLE_SCENES.has(loc.scene)) ? loc.scene : SCENE.MAIN;
    const data = {};
    if (loc?.x != null) data.spawnX = loc.x;
    if (loc?.y != null) data.spawnY = loc.y;
    if (Number.isInteger(loc?.facing)) data.spawnFacing = loc.facing;   // animated-walk prototype: face the way she faced
    this.scene.start(scene, data);
  }
}
